/**
 * Cartoon art for the DOM dialogs and ceremonies (cartoon-overlays.css), drawn in code:
 *   - ray bursts (SVG): two-tone tapered rays with a soft ink edge, spun behind rewards,
 *     the dragon, discoveries and the puzzle board;
 *   - a cel starburst plate (canvas, toon-kit) behind a reward or a wheel winner;
 *   - four-point inked sparkles (SVG) that twinkle round a prize.
 * `installOverlayArt()` publishes them as CSS custom properties on <html>, so the sheet uses
 * `background-image: var(--toon-rays-gold, <fallback>)` and nothing is fetched.
 *
 * It also keeps the lit lettering working: an inked title paints its lit fill from a copy of
 * its text in `data-text` (see `.toon-lit` in cartoon-overlays.css), so headings that game code
 * rewrites (revive title, puzzle headline, the game-over words) are re-stamped when they change.
 */
import { INK, PUFF_TONES, mixHex } from "../palette.js";
import { starburstPath, glint, inkStyle, seededRandom, TAU } from "../toon-kit.js";
import { defineToonArt, getToonArtUrl } from "../art-registry.js";

/** Ray burst colour sets: `lit` paints the leading half of each ray, `base` the trailing half. */
export var RAY_THEMES = {
  gold: { lit: "rgba(255, 247, 210, 0.8)", base: "rgba(255, 204, 76, 0.7)", ink: "rgba(23, 42, 58, 0.38)" },
  mint: { lit: "rgba(230, 255, 240, 0.78)", base: "rgba(98, 226, 160, 0.66)", ink: "rgba(23, 42, 58, 0.38)" },
  sky: { lit: "rgba(236, 250, 255, 0.72)", base: "rgba(110, 206, 246, 0.6)", ink: "rgba(23, 42, 58, 0.34)" },
};

/**
 * Two-tone tapered rays round the origin, as an SVG document (viewBox -100..100).
 * Each ray is a wedge from the centre out to radius 100 with a rounded end; its leading half
 * is lit and its trailing half is the base tone, so the burst reads as cel-shaded facets.
 * @param {{ rays?: number, width?: number, lit: string, base: string, ink: string, inkWidth?: number }} o
 *   width: share of each ray's slot the ray fills (0–1)
 * @returns {string}
 */
export function rayBurstSvg(o) {
  var n = Math.max(3, Math.floor(o.rays || 16));
  var half = (Math.PI / n) * (o.width != null ? o.width : 0.5);
  var R = 100;
  var lit = "", base = "", ink = "";
  for (var i = 0; i < n; i++) {
    var a = -Math.PI / 2 + (i / n) * TAU;
    var l = pt(R, a - half), m = pt(R, a), r = pt(R, a + half);
    lit += "M0 0L" + l + "A" + R + " " + R + " 0 0 1 " + m + "Z";
    base += "M0 0L" + m + "A" + R + " " + R + " 0 0 1 " + r + "Z";
    ink += "M0 0L" + l + "A" + R + " " + R + " 0 0 1 " + r + "Z";
  }
  return "<svg xmlns='http://www.w3.org/2000/svg' viewBox='-100 -100 200 200'>"
    + "<path d='" + lit + "' fill='" + o.lit + "'/>"
    + "<path d='" + base + "' fill='" + o.base + "'/>"
    + "<path d='" + ink + "' fill='none' stroke='" + o.ink + "' stroke-width='" + (o.inkWidth || 0.9)
    + "' stroke-linejoin='round'/>"
    + "</svg>";
}

function pt(r, a) {
  return (Math.cos(a) * r).toFixed(2) + " " + (Math.sin(a) * r).toFixed(2);
}

/**
 * A four-point sparkle with concave sides and an ink rim (viewBox -12..12), like toon-kit's
 * `sparklePath`, plus a white glint dot.
 * @param {string} fill
 * @param {string} [ink]
 * @returns {string}
 */
export function sparkleSvg(fill, ink) {
  var k = 2.2; // how far the concave sides bow toward the centre
  var d = "M0 -10.5Q" + k + " -" + k + " 10.5 0Q" + k + " " + k + " 0 10.5Q-" + k + " " + k
    + " -10.5 0Q-" + k + " -" + k + " 0 -10.5Z";
  return "<svg xmlns='http://www.w3.org/2000/svg' viewBox='-12 -12 24 24'>"
    + "<path d='" + d + "' fill='" + fill + "' stroke='" + (ink || INK) + "' stroke-width='1.5' stroke-linejoin='round'/>"
    + "<circle cx='-1.6' cy='-2.6' r='1.3' fill='#ffffff'/>"
    + "</svg>";
}

/** Confetti colours: the candy palette of the buttons and the wheel. */
var CONFETTI_COLORS = ["#ff6b6b", "#ffd04d", "#45c4f0", "#4fd189", "#b07cf0", "#ff8fc6", "#ff9a45"];

/** Confetti tile size (SVG units; CSS shows it at this many --ov-u units). */
export var CONFETTI_TILE = [360, 420];

/**
 * A repeating tile of inked confetti: chips, dots and squiggles, each painted twice (a fatter
 * ink copy under its colour), one per cell of a cols x rows grid with a seeded jitter, so the
 * pieces spread evenly without the repeat reading as a lattice. It is shown as a background
 * that slides down one tile height per loop, so it tiles without a seam.
 * @param {number} [seed]
 * @param {number} [cols]
 * @param {number} [rows]
 * @returns {string}
 */
export function confettiSvg(seed, cols, rows) {
  var rnd = seededRandom(seed || 7);
  var nx = cols || 3, ny = rows || 6;
  var cw = CONFETTI_TILE[0] / nx, ch = CONFETTI_TILE[1] / ny;
  var shapes = "";
  for (var i = 0; i < nx * ny; i++) {
    var x = ((i % nx) * cw + cw * (0.15 + rnd() * 0.7)).toFixed(1);
    var y = (Math.floor(i / nx) * ch + ch * (0.15 + rnd() * 0.7)).toFixed(1);
    var rot = Math.round(rnd() * 180);
    var col = CONFETTI_COLORS[Math.floor(rnd() * CONFETTI_COLORS.length)];
    var at = "translate(" + x + " " + y + ") rotate(" + rot + ")";
    var kind = Math.floor(rnd() * 3);
    if (kind === 0) {
      shapes += "<rect x='-5.5' y='-3.2' width='11' height='6.4' rx='1.5' transform='" + at + "' fill='" + INK
        + "' stroke='" + INK + "' stroke-width='2.6'/>"
        + "<rect x='-5.5' y='-3.2' width='11' height='6.4' rx='1.5' transform='" + at + "' fill='" + col + "'/>";
    } else if (kind === 1) {
      shapes += "<circle r='3.8' transform='" + at + "' fill='" + INK + "' stroke='" + INK + "' stroke-width='2.6'/>"
        + "<circle r='3.8' transform='" + at + "' fill='" + col + "'/>";
    } else {
      var d = "M-6.5 -2Q-3.2 -6.5 0 -2T6.5 -2";
      shapes += "<path d='" + d + "' transform='" + at + "' fill='none' stroke='" + INK
        + "' stroke-width='6' stroke-linecap='round'/>"
        + "<path d='" + d + "' transform='" + at + "' fill='none' stroke='" + col
        + "' stroke-width='3.3' stroke-linecap='round'/>";
    }
  }
  return "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 " + CONFETTI_TILE[0] + " " + CONFETTI_TILE[1]
    + "' stroke-linejoin='round'>" + shapes + "</svg>";
}

/** `url("data:image/svg+xml,…")` for a CSS background. */
export function svgCssUrl(svg) {
  return "url(\"data:image/svg+xml," + encodeURIComponent(svg) + "\")";
}

/**
 * Cel starburst plate: a spiky star in gold with a lit body shifted up-left, a lit core,
 * an ink rim and a glint. Drawn once through the shared registry (blob URL for CSS).
 */
var BURST_KEY = "overlay/burst-gold";
defineToonArt(BURST_KEY, {
  w: 256,
  h: 256,
  urlScale: 2,
  name: "overlay-burst-gold",
  draw: function (c, w, h) {
    drawStarburstPlate(c, w / 2, h / 2, Math.min(w, h) * 0.47, PUFF_TONES.hot);
  },
});

/**
 * @param {CanvasRenderingContext2D} c
 * @param {number} x
 * @param {number} y
 * @param {number} r
 * @param {{ base: string, light: string, shade: string }} tone
 */
export function drawStarburstPlate(c, x, y, r, tone) {
  var opts = { n: 14, inner: 0.74, seed: 7 };
  // Shade first, then the lit body nudged up-left and clipped to the star: a lower-right crescent.
  c.save();
  starburstPath(c, x, y, r, opts);
  c.fillStyle = tone.shade;
  c.fill();
  c.clip();
  starburstPath(c, x - r * 0.06, y - r * 0.08, r * 0.97, opts);
  c.fillStyle = tone.base;
  c.fill();
  c.beginPath();
  c.arc(x - r * 0.1, y - r * 0.12, r * 0.5, 0, TAU);
  c.fillStyle = mixHex(tone.base, tone.light, 0.65);
  c.fill();
  c.restore();
  starburstPath(c, x, y, r, opts);
  inkStyle(c, r * 0.045, INK);
  c.stroke();
  glint(c, x - r * 0.42, y - r * 0.4, r * 0.08);
}

var installed = false;

/**
 * Publish the overlay art as CSS custom properties on <html> (idempotent) and start keeping
 * the lit lettering in step with game-written headings.
 * @param {Document} [doc]
 */
export function installOverlayArt(doc) {
  doc = doc || (typeof document !== "undefined" ? document : null);
  if (!doc || !doc.documentElement || installed) return;
  installed = true;
  var style = doc.documentElement.style;
  style.setProperty("--toon-rays-gold", svgCssUrl(rayBurstSvg(Object.assign({ rays: 18 }, RAY_THEMES.gold))));
  style.setProperty("--toon-rays-mint", svgCssUrl(rayBurstSvg(Object.assign({ rays: 16 }, RAY_THEMES.mint))));
  style.setProperty("--toon-rays-sky", svgCssUrl(rayBurstSvg(Object.assign({ rays: 20, width: 0.46 }, RAY_THEMES.sky))));
  style.setProperty("--toon-sparkle-gold", svgCssUrl(sparkleSvg("#ffe27a")));
  style.setProperty("--toon-sparkle-white", svgCssUrl(sparkleSvg("#ffffff")));
  style.setProperty("--toon-sparkle-mint", svgCssUrl(sparkleSvg("#a8f2c6")));
  style.setProperty("--toon-confetti-a", svgCssUrl(confettiSvg(7, 3, 5)));
  style.setProperty("--toon-confetti-b", svgCssUrl(confettiSvg(31, 4, 5)));
  // The plate is a canvas encoded to PNG: do it when the browser is idle, not during boot
  // (it is only needed once a reward shows; the CSS has a flat fallback until then).
  var publishBurst = function () {
    try {
      var burst = getToonArtUrl(BURST_KEY);
      if (burst && burst.indexOf("toon-art/") !== 0) style.setProperty("--toon-burst-gold", "url(\"" + burst + "\")");
    } catch (_e) {
      // The page went away first (tests, navigation): the flat CSS fallback stays.
    }
  };
  var view = doc.defaultView;
  if (view && typeof view.requestIdleCallback === "function") view.requestIdleCallback(publishBurst, { timeout: 2500 });
  else setTimeout(publishBurst, 1200);
  stampInkedLettering(doc);
  watchInkedLettering(doc, LIT_WATCH_IDS);
  bindSliderFill(doc, "fallVolumeOverlay", ".fall-volume-slider");
}

/**
 * Keep `--fill` (the tube's filled share) on each range input in step with its value. Game
 * code sets the values before opening the dialog without an input event, so the fill is also
 * re-read whenever the dialog's class list changes (it loses `hidden` when it opens).
 * @param {Document} doc
 * @param {string} overlayId
 * @param {string} selector
 */
export function bindSliderFill(doc, overlayId, selector) {
  var overlay = doc.getElementById(overlayId);
  if (!overlay || typeof overlay.querySelectorAll !== "function") return;
  var sliders = overlay.querySelectorAll(selector);
  var syncAll = function () {
    for (var i = 0; i < sliders.length; i++) syncSliderFill(sliders[i]);
  };
  for (var i = 0; i < sliders.length; i++) {
    sliders[i].addEventListener("input", onSliderInput);
  }
  syncAll();
  if (typeof MutationObserver !== "undefined") {
    new MutationObserver(syncAll).observe(overlay, { attributes: true, attributeFilter: ["class"] });
  }
}

function onSliderInput(e) {
  syncSliderFill(e.currentTarget);
}

/** @param {HTMLInputElement} el */
export function syncSliderFill(el) {
  if (!el || !el.style) return;
  var min = Number(el.min) || 0;
  var max = Number(el.max);
  if (!isFinite(max) || max <= min) max = 100;
  var v = Math.max(min, Math.min(max, Number(el.value) || 0));
  el.style.setProperty("--fill", ((v - min) / (max - min)) * 100 + "%");
}

/** Headings whose text game code rewrites after boot (outside the overlay modules). */
export var LIT_WATCH_IDS = ["liveReviveTitle", "puzzleRevealHeadline", "gameOverTitle"];

/** Elements that paint lit lettering from `data-text`. */
var LIT_SELECTOR = ".toon-lit, .victory-overlay__headline-word";

/**
 * Copy each lit element's text into `data-text` (the lit fill layer reads it).
 * @param {ParentNode} root
 */
export function stampInkedLettering(root) {
  if (!root || typeof root.querySelectorAll !== "function") return;
  var list = root.querySelectorAll(LIT_SELECTOR);
  for (var i = 0; i < list.length; i++) syncLitText(list[i]);
  if (root.matches && root.matches(LIT_SELECTOR)) syncLitText(root);
}

/** @param {Element} el */
export function syncLitText(el) {
  if (!el || typeof el.getAttribute !== "function") return;
  // Headline words are split by spans; only leaf text belongs in the copy.
  if (el.classList && el.classList.contains("toon-lit") && el.querySelector && el.querySelector(".victory-overlay__headline-word")) {
    el.removeAttribute("data-text");
    return;
  }
  var text = el.textContent || "";
  if (el.getAttribute("data-text") !== text) el.setAttribute("data-text", text);
}

/**
 * Re-stamp the lit copy whenever game code rewrites one of these headings.
 * @param {Document} doc
 * @param {string[]} ids
 */
export function watchInkedLettering(doc, ids) {
  if (typeof MutationObserver === "undefined") return;
  var obs = new MutationObserver(function (records) {
    for (var i = 0; i < records.length; i++) {
      var t = records[i].target;
      var el = t && t.nodeType === 3 ? t.parentElement : t;
      var host = el && el.closest ? el.closest("[id]") : null;
      if (host) stampInkedLettering(host);
    }
  });
  for (var j = 0; j < ids.length; j++) {
    var el = doc.getElementById(ids[j]);
    if (el) obs.observe(el, { childList: true, characterData: true, subtree: true });
  }
}

/** @internal Test helper. */
export function __resetOverlayArtForTests() {
  installed = false;
}
